import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, Music, Instagram, Twitter, Youtube, Globe, 
  Play, X, ChevronLeft, ChevronRight, Calendar, Users, ArrowRight, Download
} from 'lucide-react';
import { FaSpotify } from 'react-icons/fa';
import { Layout } from '../components/layout/Layout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, onNext, onPrev]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/60 hover:text-white z-10"
      >
        <X size={32} />
      </button>
      
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-6 text-white/60 hover:text-white z-10"
      >
        <ChevronLeft size={48} />
      </button>
      
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-6 text-white/60 hover:text-white z-10"
      >
        <ChevronRight size={48} />
      </button>
      
      <img
        src={images[currentIndex]}
        alt=""
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      
      <div className="absolute bottom-6 text-white/60 text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </motion.div>
  );
};

export default function ArtistDetailPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [relatedArtists, setRelatedArtists] = useState([]);

  useEffect(() => {
    const fetchArtist = async () => {
      try {
        const response = await axios.get(`${API}/public/artists/${slug}`);
        setArtist(response.data);
        
        // Fetch related artists
        const relatedResponse = await axios.get(`${API}/public/artists?limit=4`);
        setRelatedArtists(relatedResponse.data.items?.filter(a => a.slug !== slug).slice(0, 3) || []);
      } catch (error) {
        console.error('Error fetching artist:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArtist();
  }, [slug]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    if (artist?.gallery?.length) {
      setLightboxIndex((prev) => (prev + 1) % artist.gallery.length);
    }
  };

  const prevImage = () => {
    if (artist?.gallery?.length) {
      setLightboxIndex((prev) => (prev - 1 + artist.gallery.length) % artist.gallery.length);
    }
  };

  const socialIcons = {
    instagram: Instagram,
    twitter: Twitter,
    youtube: Youtube,
    spotify: FaSpotify,
    website: Globe
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!artist) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-display text-4xl text-white mb-4">Artista no encontrado</h1>
            <Link to="/talent" className="btn-gold">Ver Talento</Link>
          </div>
        </div>
      </Layout>
    );
  }

  const galleryImages = artist.gallery?.length > 0 
    ? artist.gallery 
    : [artist.cover_image || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&q=80'];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end" data-testid="artist-hero">
        <div className="absolute inset-0">
          <img
            src={artist.cover_image || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1920&q=80'}
            alt={artist.name?.[lang] || artist.name?.es}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        </div>

        <div className="container-premium relative pb-16 pt-32">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-2 mb-4">
              {artist.genres?.map((genre) => (
                <span key={genre} className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs rounded-full uppercase tracking-wider">
                  {genre}
                </span>
              ))}
            </motion.div>
            
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl lg:text-8xl text-white mb-4">
              {artist.name?.[lang] || artist.name?.es}
            </motion.h1>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-6 text-white/60">
              <span className="flex items-center gap-2">
                <MapPin size={18} className="text-[#D4AF37]" />
                {artist.city}, {artist.country}
              </span>
              {artist.format && (
                <span className="flex items-center gap-2">
                  <Music size={18} className="text-[#D4AF37]" />
                  {artist.format}
                </span>
              )}
              {artist.categories?.map((cat) => (
                <span key={cat} className="px-3 py-1 bg-white/10 rounded-full text-sm">
                  {cat}
                </span>
              ))}
            </motion.div>

            {/* Social Links */}
            {Object.keys(artist.social_links || {}).length > 0 && (
              <motion.div variants={fadeInUp} className="flex items-center gap-3 mt-6">
                {Object.entries(artist.social_links).map(([platform, url]) => {
                  const Icon = socialIcons[platform] || Globe;
                  return url ? (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-colors"
                    >
                      <Icon size={18} />
                    </a>
                  ) : null;
                })}
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-dark py-20" data-testid="artist-content">
        <div className="container-premium">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-16">
              {/* Biography */}
              {(artist.biography?.es || artist.biography?.en) && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <h2 className="font-display text-3xl text-white mb-6">
                    {lang === 'es' ? 'Biografía' : 'Biography'}
                  </h2>
                  <p className="text-white/70 leading-relaxed text-lg whitespace-pre-line">
                    {artist.biography?.[lang] || artist.biography?.es}
                  </p>
                </motion.div>
              )}

              {/* Gallery */}
              {galleryImages.length > 0 && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <h2 className="font-display text-3xl text-white mb-6">
                    {lang === 'es' ? 'Galería' : 'Gallery'}
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {galleryImages.map((image, index) => (
                      <div
                        key={index}
                        className="aspect-square overflow-hidden cursor-pointer group"
                        onClick={() => openLightbox(index)}
                      >
                        <img
                          src={image}
                          alt={`${artist.name?.[lang]} - ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Videos */}
              {artist.videos?.length > 0 && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <h2 className="font-display text-3xl text-white mb-6">Videos</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {artist.videos.map((video, index) => (
                      <div key={index} className="relative aspect-video bg-black/50 rounded-lg overflow-hidden group">
                        <a 
                          href={video} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center"
                        >
                          <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Play size={24} className="text-black ml-1" fill="black" />
                          </div>
                        </a>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Booking CTA */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="glass-dark rounded-2xl p-8 sticky top-32"
              >
                <h3 className="font-display text-2xl text-white mb-4">
                  {lang === 'es' ? 'Solicitar Booking' : 'Request Booking'}
                </h3>
                <p className="text-white/60 mb-6">
                  {lang === 'es' 
                    ? '¿Interesado en contratar a este artista para tu evento?'
                    : 'Interested in booking this artist for your event?'
                  }
                </p>
                <Link
                  to={`/contact?artist=${slug}`}
                  className="btn-gold w-full text-center block"
                  data-testid="booking-cta"
                >
                  {t('talent.book_now')}
                </Link>

                {/* Downloads */}
                {(artist.press_kit_url || artist.rider_url) && (
                  <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                    {artist.press_kit_url && (
                      <a
                        href={artist.press_kit_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-white/60 hover:text-white transition-colors"
                      >
                        <Download size={18} />
                        <span>Press Kit</span>
                      </a>
                    )}
                    {artist.rider_url && (
                      <a
                        href={artist.rider_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-white/60 hover:text-white transition-colors"
                      >
                        <Download size={18} />
                        <span>Technical Rider</span>
                      </a>
                    )}
                  </div>
                )}
              </motion.div>

              {/* Quick Info */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="glass-dark rounded-2xl p-8"
              >
                <h4 className="text-white/50 text-xs uppercase tracking-wider mb-4">
                  {lang === 'es' ? 'Información' : 'Information'}
                </h4>
                <ul className="space-y-4">
                  <li className="flex justify-between">
                    <span className="text-white/50">{lang === 'es' ? 'Ubicación' : 'Location'}</span>
                    <span className="text-white">{artist.city}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-white/50">{lang === 'es' ? 'Formato' : 'Format'}</span>
                    <span className="text-white">{artist.format || 'Live'}</span>
                  </li>
                  {artist.genres?.length > 0 && (
                    <li className="flex justify-between">
                      <span className="text-white/50">{lang === 'es' ? 'Géneros' : 'Genres'}</span>
                      <span className="text-white text-right">{artist.genres.slice(0, 2).join(', ')}</span>
                    </li>
                  )}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Artists */}
      {relatedArtists.length > 0 && (
        <section className="section-subtle py-20" data-testid="related-artists">
          <div className="container-premium">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.div variants={fadeInUp} className="flex items-end justify-between mb-12">
                <h2 className="font-display text-3xl text-white">
                  {lang === 'es' ? 'Artistas Relacionados' : 'Related Artists'}
                </h2>
                <Link to="/talent" className="text-white/60 hover:text-white flex items-center gap-2">
                  {t('talent.view_all')}
                  <ArrowRight size={16} />
                </Link>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArtists.map((relatedArtist) => (
                  <motion.div key={relatedArtist.slug} variants={fadeInUp}>
                    <Link to={`/talent/${relatedArtist.slug}`} className="block group">
                      <div className="relative aspect-[3/4] overflow-hidden mb-4">
                        <img
                          src={relatedArtist.cover_image || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&q=80'}
                          alt={relatedArtist.name?.[lang] || relatedArtist.name?.es}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-5">
                          <p className="text-[#D4AF37] text-xs tracking-wider uppercase mb-1">
                            {relatedArtist.categories?.[0]}
                          </p>
                          <h3 className="font-display text-xl text-white">
                            {relatedArtist.name?.[lang] || relatedArtist.name?.es}
                          </h3>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <Lightbox
            images={galleryImages}
            currentIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
            onNext={nextImage}
            onPrev={prevImage}
          />
        )}
      </AnimatePresence>
    </Layout>
  );
}
